/**
 * @file DealItemsTab.tsx
 * @description Вкладка «Позиции» в карточке Deal (phase10.5 шаг 2.6).
 *
 * Менеджер собирает черновой состав на стадиях Заявка / Уточнение /
 * Согласование. UX зеркалит OrderCreatePage:
 *  - inline-autocomplete в строке для пикера товара
 *  - Sheet каталога с множественным выбором
 *  - при создании Order из этой Deal позиции пред-заполнятся
 *
 * Если Deal уже превращена в Order (resultingOrderId !== null) — секция
 * read-only, показывается алерт с ссылкой на Order.
 */

import { List, Loader2, Package, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { OrderItemRowInput } from "../orders/OrderItemRowInput";
import { SkuPickerDialog } from "../orders/SkuPickerDialog";
import { Alert } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { EditorialCard } from "../ui/EditorialCard";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import {
  addDealItem,
  addDealItemsBulk,
  listDealItems,
  removeDealItem,
  updateDealItem,
  type DealDto,
  type DealItemDto,
} from "../../lib/api-crm";
import { formatMoney, type OrderSkuDto } from "../../lib/api-orders";
import { qtyInputProps, skuCountUnit, validateQty } from "../../lib/qty-input";

interface Props {
  deal: DealDto;
  onChanged?: () => void;
}

export function DealItemsTab({ deal, onChanged }: Props) {
  const notify = useNotify();
  const [items, setItems] = useState<DealItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Пустая строка с inline-input для нового SKU (до выбора).
  const [emptyRowOpen, setEmptyRowOpen] = useState(false);

  const readOnly = !!deal.resultingOrderId;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await listDealItems(deal.id);
        if (!cancelled) {
          setItems(res);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : "Не удалось загрузить позиции",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deal.id]);

  async function pickSkuForEmptyRow(sku: OrderSkuDto) {
    try {
      const created = await addDealItem(deal.id, { skuId: sku.id });
      setItems((prev) => [...prev, created]);
      setEmptyRowOpen(false);
      onChanged?.();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось добавить");
    }
  }

  async function addFromCatalog(skus: OrderSkuDto[]) {
    try {
      await addDealItemsBulk(
        deal.id,
        skus.map((s) => s.id),
      );
      // Перезагрузим, чтобы корректно показать дубли (qty++).
      const res = await listDealItems(deal.id);
      setItems(res);
      setPickerOpen(false);
      onChanged?.();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось добавить");
    }
  }

  async function patchItem(item: DealItemDto, patch: Partial<DealItemDto>) {
    try {
      const updated = await updateDealItem(deal.id, item.id, {
        quantity: patch.quantity,
        priceGross: patch.priceGross,
        note: patch.note ?? undefined,
      });
      setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
      onChanged?.();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось обновить");
    }
  }

  async function removeItem(item: DealItemDto) {
    try {
      await removeDealItem(deal.id, item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      onChanged?.();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить");
    }
  }

  const total = items.reduce(
    (sum, i) => sum + Number(i.totalGross || 0),
    0,
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="size-5 animate-spin text-on-surface-variant" />
      </div>
    );
  }

  return (
    <EditorialCard>
      <EditorialCard.Header
        title="Позиции черновика"
        subtitle={
          readOnly
            ? "Сделка превращена в заказ — позиции редактируются в карточке заказа."
            : "Подберите состав. Клиент увидит этот список в ЛК при согласовании."
        }
        action={
          readOnly ? undefined : (
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEmptyRowOpen(true)}
              >
                <Plus className="size-4 mr-1" />
                Позиция
              </Button>
              <Button size="sm" onClick={() => setPickerOpen(true)}>
                <List className="size-4 mr-1" />
                Из каталога
              </Button>
            </div>
          )
        }
      />
      <EditorialCard.Body>
        {error ? <Alert severity="error">{error}</Alert> : null}

        {items.length === 0 && !emptyRowOpen ? (
          <div className="text-center py-8 text-on-surface-variant text-sm">
            {readOnly
              ? "Позиций нет."
              : "Добавьте первую позицию через «+ Позиция» или «Из каталога»."}
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((r) => (
              <li
                key={r.id}
                className="flex gap-3 items-center flex-wrap p-3 rounded bg-surface/40"
              >
                <Package className="size-4 text-on-surface-variant shrink-0" />
                <div className="flex-1 min-w-40">
                  <div className="font-medium text-sm">{r.productName}</div>
                  {r.skuCode ? (
                    <Badge
                      variant="outline"
                      className="text-[10px] font-mono mt-0.5"
                    >
                      {r.skuCode}
                    </Badge>
                  ) : null}
                </div>
                <div className="w-24 flex flex-col gap-1">
                  <Label className="text-[10px]">Кол-во, {skuCountUnit(r)}</Label>
                  <Input
                    type="number"
                    step={qtyInputProps(skuCountUnit(r)).step}
                    inputMode={qtyInputProps(skuCountUnit(r)).inputMode}
                    min="0"
                    disabled={readOnly}
                    defaultValue={r.quantity}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if (v === r.quantity) return;
                      // Пачек «1.5» не бывает: возвращаем прежнее значение и
                      // объясняем, а не отправляем серверу заведомый мусор.
                      const message = validateQty(v, skuCountUnit(r), {
                        allowZero: false,
                      });
                      if (message) {
                        notify.error(message);
                        e.target.value = r.quantity;
                        return;
                      }
                      patchItem(r, { quantity: v });
                    }}
                  />
                </div>
                <div className="w-32 flex flex-col gap-1">
                  <Label className="text-[10px]">Цена gross, ₽</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    disabled={readOnly}
                    defaultValue={r.priceGross}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if (v !== r.priceGross) patchItem(r, { priceGross: v });
                    }}
                  />
                </div>
                <div className="w-28 text-right">
                  <div className="text-[10px] text-on-surface-variant">Сумма</div>
                  <div className="font-semibold text-primary">
                    {formatMoney(r.totalGross)} ₽
                  </div>
                </div>
                {readOnly ? null : (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => removeItem(r)}
                    aria-label="Удалить позицию"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </li>
            ))}

            {emptyRowOpen && !readOnly ? (
              <li className="flex gap-3 items-center p-3 rounded bg-surface/40">
                <div className="flex-1 min-w-60">
                  <OrderItemRowInput onSelect={pickSkuForEmptyRow} />
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setEmptyRowOpen(false)}
                  aria-label="Отмена"
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ) : null}
          </ul>
        )}

        <div className="mt-4 text-right text-xl font-semibold tabular-nums text-primary">
          Итого: {formatMoney(String(total))} ₽
        </div>
      </EditorialCard.Body>

      {pickerOpen ? (
        <SkuPickerDialog
          onClose={() => setPickerOpen(false)}
          onSelectMany={addFromCatalog}
        />
      ) : null}
    </EditorialCard>
  );
}
